export interface SurveyReportOptionSummary {
  value: string
  label: string
  count: number
  percentage: number
  weight?: number
}

export interface SurveyReportQuestionSummary {
  key: string
  questionName: string
  questionTitle: string
  rowTitle?: string
  type: string
  answerCount: number
  totalResponseCount: number
  average?: number | null
  normalizedAverage?: number | null
  options: SurveyReportOptionSummary[]
  answers?: string[]
  sampleAnswers?: string[]
}

export interface SurveyReportSummary {
  surveyName: string
  semesterName: string
  generatedAt: string
  surveyEndAt?: string | null
  responseCount: number
  uniqueParticipantCount: number
  overallAverage: number | null
  likertItems: SurveyReportQuestionSummary[]
  choiceItems: SurveyReportQuestionSummary[]
  openEndedItems: SurveyReportQuestionSummary[]
  strongestItems: SurveyReportQuestionSummary[]
  improvementItems: SurveyReportQuestionSummary[]
}

interface BuildSurveyReportSummaryArgs {
  surveyJson: any
  surveyData: any[]
  surveyName: string
  semesterName: string
  generatedAt: string
  surveyEndAt?: string | null
  uniqueParticipantCount: number
}

const EMPTY_TITLE = 'Başlıksız soru'

export const resolveSurveyText = (source: any): string => {
  if (!source) return ''

  if (typeof source === 'string') return source

  if (typeof source === 'number' || typeof source === 'boolean') return String(source)

  if (typeof source === 'object') {
    if (typeof source.tr === 'string') return source.tr
    if (typeof source.default === 'string') return source.default

    const firstText = Object.values(source).find((value) => typeof value === 'string')
    if (typeof firstText === 'string') return firstText
  }

  return ''
}

const getDisplayText = (item: any, fallback: string) => {
  if (!item) return fallback

  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return String(item)
  }

  return (
    resolveSurveyText(item.title) ||
    resolveSurveyText(item.text) ||
    resolveSurveyText(item.name) ||
    resolveSurveyText(item.value) ||
    fallback
  )
}

const getItemValue = (item: any, fallback: string | number) => {
  if (item && typeof item === 'object' && item.value !== undefined && item.value !== null) {
    return item.value
  }

  return item ?? fallback
}

const asArray = (value: any): any[] => (Array.isArray(value) ? value : [])

const getSurveyElements = (surveyJson: any): any[] => {
  const pages = asArray(surveyJson?.pages)
  return pages.flatMap((page: any) => asArray(page?.elements))
}

const toNumberOrNull = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.')
    const parsed = Number(normalized)
    if (Number.isFinite(parsed)) return parsed
  }

  return null
}

const buildWeights = (options: SurveyReportOptionSummary[]) => {
  const numericValues = options.map((option) => toNumberOrNull(option.value))
  const allNumeric = numericValues.every((value) => value !== null)

  return options.map((option, index) => ({
    ...option,
    weight: allNumeric ? numericValues[index]! : index + 1,
  }))
}

const createOptionSummaries = (
  rawOptions: any[],
  answers: any[],
  fallbackPrefix = 'Seçenek'
): SurveyReportOptionSummary[] => {
  const normalizedOptions = rawOptions.map((option, index) => {
    const value = getItemValue(option, index + 1)
    return {
      value: String(value),
      label: getDisplayText(option, `${fallbackPrefix} ${index + 1}`),
      count: 0,
      percentage: 0,
    }
  })

  const total = answers.length
  const optionsWithCounts = normalizedOptions.map((option) => {
    const count = answers.filter((answer) => String(answer) === option.value).length
    return {
      ...option,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }
  })

  return buildWeights(optionsWithCounts)
}

const getAverage = (options: SurveyReportOptionSummary[]) => {
  const total = options.reduce((sum, option) => sum + option.count, 0)
  if (total === 0) return {average: null, normalizedAverage: null}

  const weighted = options.reduce((sum, option) => sum + option.count * (option.weight ?? 0), 0)
  const average = weighted / total
  const weights = options
    .map((option) => option.weight)
    .filter((weight): weight is number => typeof weight === 'number' && Number.isFinite(weight))

  if (weights.length < 2) return {average, normalizedAverage: null}

  const min = Math.min(...weights)
  const max = Math.max(...weights)
  const normalizedAverage = max > min ? ((average - min) / (max - min)) * 100 : null

  return {average, normalizedAverage}
}

const isAnsweredValue = (value: any) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return `${value}`.trim().length > 0
}

const createQuestionSummary = (
  args: {
    key: string
    questionName: string
    questionTitle: string
    rowTitle?: string
    type: string
    answers: any[]
    options: SurveyReportOptionSummary[]
    totalResponseCount: number
  }
): SurveyReportQuestionSummary => {
  const {average, normalizedAverage} = getAverage(args.options)
  return {
    key: args.key,
    questionName: args.questionName,
    questionTitle: args.questionTitle || EMPTY_TITLE,
    rowTitle: args.rowTitle,
    type: args.type,
    answerCount: args.answers.length,
    totalResponseCount: args.totalResponseCount,
    average,
    normalizedAverage,
    options: args.options,
  }
}

const buildRatingOptions = (question: any) => {
  if (Array.isArray(question?.rateValues) && question.rateValues.length > 0) {
    return question.rateValues
  }

  const rateMin = Number.isFinite(Number(question?.rateMin)) ? Number(question.rateMin) : 1
  const rateMax = Number.isFinite(Number(question?.rateMax))
    ? Number(question.rateMax)
    : Number.isFinite(Number(question?.rateCount))
      ? Number(question.rateCount)
      : 5

  return Array.from({length: Math.max(0, rateMax - rateMin + 1)}, (_, index) => {
    const value = rateMin + index
    return {value, text: String(value)}
  })
}

export const buildSurveyReportSummary = ({
  surveyJson,
  surveyData,
  surveyName,
  semesterName,
  generatedAt,
  surveyEndAt,
  uniqueParticipantCount,
}: BuildSurveyReportSummaryArgs): SurveyReportSummary => {
  const sanitizedData = Array.isArray(surveyData)
    ? surveyData.filter((entry: any) => entry && typeof entry === 'object')
    : []
  const totalResponseCount = sanitizedData.length

  const likertItems: SurveyReportQuestionSummary[] = []
  const choiceItems: SurveyReportQuestionSummary[] = []
  const openEndedItems: SurveyReportQuestionSummary[] = []

  getSurveyElements(surveyJson).forEach((question: any) => {
    if (!question || !question.name) return

    const questionName = String(question.name)
    const questionTitle = getDisplayText(question, questionName)
    const questionType = question.type || 'unknown'

    if (questionType === 'matrix') {
      const rows = asArray(question.rows)
      const columns = asArray(question.columns)
      if (!rows.length || !columns.length) return

      rows.forEach((row: any, rowIndex: number) => {
        const rowValue = getItemValue(row, rowIndex + 1)
        const rowTitle = getDisplayText(row, String(rowValue))
        const answers = sanitizedData
          .map((entry: any) => entry?.[questionName]?.[rowValue])
          .filter(isAnsweredValue)

        const options = createOptionSummaries(columns, answers)

        likertItems.push(
          createQuestionSummary({
            key: `${questionName}__${rowValue}`,
            questionName,
            questionTitle,
            rowTitle,
            type: questionType,
            answers,
            options,
            totalResponseCount,
          })
        )
      })

      return
    }

    if (questionType === 'rating') {
      const answers = sanitizedData.map((entry: any) => entry?.[questionName]).filter(isAnsweredValue)
      const options = createOptionSummaries(buildRatingOptions(question), answers)

      likertItems.push(
        createQuestionSummary({
          key: questionName,
          questionName,
          questionTitle,
          type: questionType,
          answers,
          options,
          totalResponseCount,
        })
      )
      return
    }

    if (['radiogroup', 'dropdown'].includes(questionType) && Array.isArray(question.choices)) {
      const answers = sanitizedData.map((entry: any) => entry?.[questionName]).filter(isAnsweredValue)
      const options = createOptionSummaries(question.choices, answers)

      choiceItems.push(
        createQuestionSummary({
          key: questionName,
          questionName,
          questionTitle,
          type: questionType,
          answers,
          options,
          totalResponseCount,
        })
      )
      return
    }

    if (questionType === 'checkbox' && Array.isArray(question.choices)) {
      const selectedAnswers = sanitizedData
        .flatMap((entry: any) => {
          const value = entry?.[questionName]
          return Array.isArray(value) ? value : isAnsweredValue(value) ? [value] : []
        })
        .filter(isAnsweredValue)

      const options = createOptionSummaries(question.choices, selectedAnswers)

      choiceItems.push(
        createQuestionSummary({
          key: questionName,
          questionName,
          questionTitle,
          type: questionType,
          answers: selectedAnswers,
          options,
          totalResponseCount,
        })
      )
      return
    }

    if (['text', 'comment'].includes(questionType)) {
      const answers = sanitizedData
        .map((entry: any) => entry?.[questionName])
        .filter(isAnsweredValue)
        .map((answer: any) => String(answer).trim())

      openEndedItems.push({
        key: questionName,
        questionName,
        questionTitle,
        type: questionType,
        answerCount: answers.length,
        totalResponseCount,
        average: null,
        normalizedAverage: null,
        options: [],
        answers,
        sampleAnswers: answers.slice(0, 5),
      })
    }
  })

  const scoredLikertItems = likertItems.filter(
    (item) => typeof item.normalizedAverage === 'number' && item.answerCount > 0
  )
  const overallAverage =
    scoredLikertItems.length > 0
      ? scoredLikertItems.reduce((sum, item) => sum + (item.normalizedAverage || 0), 0) /
        scoredLikertItems.length
      : null

  const strongestItems = [...scoredLikertItems]
    .sort((a, b) => (b.normalizedAverage || 0) - (a.normalizedAverage || 0))
    .slice(0, 5)

  const improvementItems = [...scoredLikertItems]
    .sort((a, b) => (a.normalizedAverage || 0) - (b.normalizedAverage || 0))
    .slice(0, 5)

  return {
    surveyName: resolveSurveyText(surveyName) || 'Anket Raporu',
    semesterName: resolveSurveyText(semesterName) || '-',
    generatedAt,
    surveyEndAt,
    responseCount: totalResponseCount,
    uniqueParticipantCount,
    overallAverage,
    likertItems,
    choiceItems,
    openEndedItems,
    strongestItems,
    improvementItems,
  }
}

export const buildSurveyAiPayload = (summary: SurveyReportSummary) => ({
  surveyName: summary.surveyName,
  semesterName: summary.semesterName,
  generatedAt: summary.generatedAt,
  surveyEndAt: summary.surveyEndAt,
  responseCount: summary.responseCount,
  uniqueParticipantCount: summary.uniqueParticipantCount,
  overallAverage: summary.overallAverage,
  strongestItems: summary.strongestItems.map((item) => ({
    title: item.rowTitle ? `${item.questionTitle} - ${item.rowTitle}` : item.questionTitle,
    answerCount: item.answerCount,
    normalizedAverage: item.normalizedAverage,
  })),
  improvementItems: summary.improvementItems.map((item) => ({
    title: item.rowTitle ? `${item.questionTitle} - ${item.rowTitle}` : item.questionTitle,
    answerCount: item.answerCount,
    normalizedAverage: item.normalizedAverage,
    frequencies: item.options.map((option) => ({
      label: option.label,
      count: option.count,
      percentage: option.percentage,
    })),
  })),
  likertFrequencies: summary.likertItems.slice(0, 80).map((item) => ({
    title: item.rowTitle ? `${item.questionTitle} - ${item.rowTitle}` : item.questionTitle,
    answerCount: item.answerCount,
    normalizedAverage: item.normalizedAverage,
    frequencies: item.options.map((option) => ({
      label: option.label,
      count: option.count,
      percentage: option.percentage,
    })),
  })),
  openEndedQuestionCount: summary.openEndedItems.length,
})
